import { useEffect, useState } from "react"

import { getOrgSignOffs } from "../../services/api/v2/signOff"
import apiSignOff from "../../services/signOff/signOff.api"
import { getObjectId } from "../../utils/utils"

/**
 * How many sign-offs wait on this person across their organisations: the
 * Home tile's number and the messages control's badge.
 *
 * @param orgs {Array<Object>}
 * @return {Promise<Number>}
 */
export async function countWaitingSignOffs(orgs = []) {
    const counts = await Promise.all(
        orgs.map((org) =>
            getOrgSignOffs(getObjectId(org._id), { approver: "me", status: ["ready"], count: true, limit: 1 })
                .then((res) => res?.count || 0)
                .catch(() => 0),
        ),
    )

    return counts.reduce((sum, n) => sum + n, 0)
}

/**
 * The waiting count, kept up as sign-offs change (one count round per burst of changes).
 *
 * @param orgs {Array<Object>}
 * @return {Number}
 */
export function useWaitingSignOffs(orgs) {
    const [count, setCount] = useState(0)

    useEffect(() => {
        if (!orgs?.length) return undefined

        let live = true
        let timer = null
        const load = () => countWaitingSignOffs(orgs).then((n) => live && setCount(n))
        const apply = () => {
            clearTimeout(timer)
            timer = setTimeout(load, 1000)
        }

        load()

        const created = apiSignOff.onChanges("create", apply)
        const updated = apiSignOff.onChanges("update", apply)

        return () => {
            live = false
            clearTimeout(timer)
            created.disconnect()
            updated.disconnect()
        }
    }, [orgs])

    return count
}
